import AppSelect from '@nxsys/components/AppSelect';
import Inheritable from '@nxsys/components/Inheritable';
import {
  defaultHolidayPayCalcOptions,
  getHolidayPaymentMethodOptions,
  holidayPaymentMethodOptions,
} from '@nxsys/constants/AppConstants';
import { HolidayMethodEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Col, Form, Row, Switch } from 'antd';
import { filter } from 'lodash';
import React from 'react';
import type { PayrollFinanceSectionProps } from './types';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import AppPercentageInput from '@nxsys/components/AppPercentageInput';

const HolidayPaySection: React.FC<PayrollFinanceSectionProps> = ({
  form,
  watchValues,
  visibility,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  if (!visibility.holidayPay) return null;

  return (
    <div className="pt-6">
      <div className="mb-6 text-xl font-medium text-gray-800">
        {messages['common.holidayPay']}
      </div>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={messages['common.holidayPayMethod']}
            name={['holidayPay', 'holidayPayMethod']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppSelect
              // NX-5228: Ireland's annual-leave rules have no 52-week-average
              // method. getHolidayPaymentMethodOptions already filters it and is
              // used elsewhere; this dropdown was still reading the raw list.
              options={getHolidayPaymentMethodOptions(isIreland)}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={messages['common.umbrellaHolidayPayMethod']}
            name={['holidayPay', 'umbrellaHolidayPayMethod']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppSelect
              options={filter(
                holidayPaymentMethodOptions,
                (op) => op.value !== 'FIFTY_TWO_WEEK_AVERAGE',
              )}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={messages['common.payeHolidayPayRate']}
            name={['holidayPay', 'payeHolidayPayRate']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppPercentageInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={messages['common.umbrellaHolidayPayRate']}
            name={['holidayPay', 'umbrellaHolidayPayRate']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppPercentageInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
      </Row>
      {/* NX-5228: dead control on Ireland. Neither
          ireland-umbrella-calculation nor ireland-tax-calculation reads
          accrueEmploymentCosts — the Irish gross-down always reserves
          hpRate x (1 + erPrsiRate) for a RETAINED holiday pot, so the toggle
          cannot turn anything off. */}
      {!isIreland && (
        <Form.Item
          name={['holidayPay', 'accrueEmploymentCosts']}
          wrapperCol={{ span: 24 }}
        >
          <Inheritable<boolean>
            label={messages['common.accrueEmploymentCosts'] as string}
            name={['holidayPay', 'accrueEmploymentCosts']}
            value={watchValues?.holidayPay?.accrueEmploymentCosts}
            inheritedValue={false}
            form={form}
          >
            {({ value, onChange, disabled }) => (
              <Switch
                checked={value ?? false}
                disabled={disabled}
                onChange={onChange}
              />
            )}
          </Inheritable>
        </Form.Item>
      )}
      {watchValues?.holidayPay?.holidayPayMethod &&
        watchValues?.holidayPay?.holidayPayMethod?.value !==
          HolidayMethodEnum.NO_HOLIDAY_PAY && (
          <Form.Item
            name={['holidayPay', 'includeInTimesheetRate']}
            wrapperCol={{ span: 24 }}
          >
            <Inheritable<boolean>
              label={
                messages['common.includedInTimesheetRatePayeOnly'] as string
              }
              name={['holidayPay', 'includeInTimesheetRate']}
              value={watchValues?.holidayPay?.includeInTimesheetRate}
              inheritedValue={false}
              form={form}
            >
              {({ value, onChange, disabled }) => (
                <Switch
                  checked={value ?? false}
                  disabled={disabled}
                  onChange={onChange}
                />
              )}
            </Inheritable>
          </Form.Item>
        )}
      {watchValues?.holidayPay?.holidayPayMethod?.value ===
        HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE && (
        <Form.Item
          label={messages['common.holidayPayCalculationRule']}
          name={['holidayPay', 'holidayPayCalculationRule']}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <AppSelect
            options={defaultHolidayPayCalcOptions}
            placeholder={messages['form.select']}
          />
        </Form.Item>
      )}
    </div>
  );
};

export default HolidayPaySection;
